// @ts-strict-ignore
import * as React from 'react';
import PageLayout from '../SettingsPageLayout';
import { AppComponentProps } from '../../typescriptHelpers';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectShouldShowAutomationsTimelineEventsOnly,
    selectIsDemoInstance,
    selectTimelineEventsFiltersMap,
} from '../../selectors/selectors';
import { useTimelineEventsPagination } from '@/utils/hooks/useTimelineEventsPagination';
import { globalMessage } from '@/utils/globalMessage';
import { filterTimelineEvent } from '../../types/timelineEventsFilters';
import { Timeline } from './TimelineEventsContainer';
import { ReleaseTimelineSectionContext } from './EventsTimeline';
import Spin from 'antd/lib/spin';
import { trackAppRouteEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import { uniq, compact } from 'lodash';

const AppTimelineEvents: React.FC<AppComponentProps> = ({ appId, orgId }) => {
    const isDemoInstance = useSelector((state: RootState) => (orgId ? selectIsDemoInstance(state, orgId) : false));

    const paginationResult = useTimelineEventsPagination({
        orgId,
        appId,
        initialLoad: !isDemoInstance,
    });

    const { events, isLoading, loadMore, error, isLoadingMore, paginationInfo } = paginationResult;

    const currentFiltersMap = useSelector((state: RootState) => selectTimelineEventsFiltersMap(state));
    const shouldShowAutomationsOnly = useSelector((state: RootState) =>
        selectShouldShowAutomationsTimelineEventsOnly(state),
    );
    const filteredEvents = events.filter((event) =>
        filterTimelineEvent(event, currentFiltersMap, shouldShowAutomationsOnly),
    );

    const availableVersions = uniq(compact(events.map((event) => event.releaseData?.displayableVersion)));

    React.useEffect(() => {
        if (error) {
            globalMessage.error('An error occurred when fetching timeline events.');
        }
    }, [error]);

    if (isLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100vh',
                    flexGrow: 1,
                }}
            >
                <Spin />
            </div>
        );
    }

    return (
        <Timeline
            releaseId={null}
            appId={appId}
            orgId={orgId}
            events={filteredEvents}
            isDrawerOpen={false}
            loadMore={loadMore}
            isLoading={isLoadingMore || isLoading}
            hasMore={paginationInfo?.hasMore}
            context={ReleaseTimelineSectionContext.AppTimeline}
            availableVersions={availableVersions}
        />
    );
};

const AppTimelineContainer: React.FC<AppComponentProps> = (props) => {
    const dispatch = useDispatch();
    React.useEffect(() => {
        dispatch(
            trackAppRouteEvent({
                orgId: props.orgId,
                appId: props.appId,
                eventName: AnalyticEvents.Event.ViewAppTimeline,
            }),
        );
    }, []);
    return <PageLayout component={AppTimelineEvents} title="Timeline" description="" {...props} />;
};

export default AppTimelineContainer;
